import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../../lib/api";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar,
} from "recharts";
import {
  Car, Users, CalendarRange, TrendingUp, AlertCircle, Activity, Wrench,
  Ban, FileSignature, Euro, ArrowDownToLine, FileText, AlertTriangle, Gavel, ShieldCheck, Shield, Clock, ShieldAlert
} from "lucide-react";
import LiveFleetTable from "../../components/dashboard/LiveFleetTable";
import WeeklyCash from "../../components/dashboard/WeeklyCash";
import { NlpCommandBar } from "../../components/NlpCommandBar";

const Mini = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
    className={`glass rounded-xl p-3.5 hover:border-[#ff0033]/30 transition-all ${accent ? "ring-1 ring-[#ff0033]/20" : ""}`}
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-2">
      <Icon size={14} className={accent ? "text-[#ff0033]" : "text-white/50"} />
      <div className="text-[9px] uppercase tracking-[0.2em] text-white/40 truncate">{label}</div>
    </div>
    <div className="font-display text-2xl font-extrabold tracking-tight leading-none">{value}</div>
    {sub && <div className="text-[10px] text-white/40 mt-1 truncate">{sub}</div>}
  </motion.div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [extras, setExtras] = useState({ contracts: [], multe: [], maint: [] });
  const [risparmio, setRisparmio] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [opsTrend, setOpsTrend] = useState([]);
  const [riskDist, setRiskDist] = useState(null);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/stats"),
      api.get("/contracts").catch(() => ({ data: [] })),
      api.get("/multe").catch(() => ({ data: [] })),
      api.get("/maintenance").catch(() => ({ data: [] })),
      api.get("/cars"),
      api.get("/rinotifiche/stats/risparmio").catch(() => ({ data: null })),
      api.get("/compliance/status").catch(() => ({ data: null })),
      api.get("/compliance/review-queue").catch(() => ({ data: { items: [] } })),
      api.get("/ops/kpi-trend?days=30").catch(() => ({ data: { series: [] } })),
      api.get("/risk/distribution").catch(() => ({ data: null })),
      api.get("/bookings").catch(() => ({ data: [] })),
    ]).then(([s, c, m, mn, ca, r, cs, rq, kt, rd, bk]) => {
      setStats(s.data);
      setExtras({ contracts: c.data, multe: m.data, maint: mn.data, cars: ca.data });
      setRisparmio(r.data);
      setCompliance({ status: cs.data, review_pending: rq.data?.items?.length || 0 });
      setOpsTrend(kt.data?.series || []);
      setRiskDist(rd.data);
      setBookings(bk.data || []);
    });
  }, []);

  // Weekly income calc
  const weekly = (() => {
    if (!stats?.revenue_chart) return null;
    const today = new Date();
    const day = today.getDay() || 7; // Mon=1..Sun=7
    const labels = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
    // Mock distribution of monthly revenue across 7 days
    const monthRev = stats.revenue_chart[stats.revenue_chart.length - 1]?.importo || 0;
    const daily = labels.map((l, i) => ({ giorno: l, importo: Math.round(monthRev / 30 * (1 + Math.random() * 0.5)) }));
    const tot = daily.reduce((s, d) => s + d.importo, 0);
    return { daily, tot };
  })();

  const cars = extras.cars || [];
  const rientriScaduti = extras.contracts?.filter((c) => c.stato === "attivo" && c.data_fine < new Date().toISOString().slice(0, 10)).length || 0;
  const scadenzeDocumenti = cars.filter((c) => {
    const today = new Date();
    const limit = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    return [c.scadenza_assicurazione, c.scadenza_revisione, c.scadenza_bollo].some((d) => d && new Date(d) <= limit);
  }).length;
  const paidUnassigned = bookings.filter((b) => b.stato === "pagata_da_assegnare");

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      <NlpCommandBar />
      {paidUnassigned.length > 0 && (
        <motion.a
          href="/admin/planning"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          data-testid="alert-paid-unassigned"
          className="flex items-center gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 hover:bg-amber-500/15 transition"
        >
          <AlertTriangle size={18} className="text-amber-400 shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-semibold text-amber-200">
              {paidUnassigned.length} prenotazion{paidUnassigned.length === 1 ? "e" : "i"} pagat{paidUnassigned.length === 1 ? "a" : "e"} senza auto assegnata
            </span>
            <span className="ml-2 text-amber-200/70 text-xs">
              Vai al Planning per assegnare la targa manualmente.
            </span>
          </div>
          <span className="text-xs text-amber-200/80 uppercase tracking-wider">Apri →</span>
        </motion.a>
      )}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Control Room</h1>
          <p className="text-sm text-white/45 mt-1">Panoramica completa — aggiornata in tempo reale.</p>
        </div>
        <div className="text-xs uppercase tracking-[0.3em] text-white/40 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#ff0033] pulse-neon" /> Live
        </div>
      </div>

      {/* Bento mini cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
        <Mini icon={Car} label="Totale Veicoli" value={stats?.cars?.totale || 0} testid="m-totale" />
        <Mini icon={Car} label="Disponibili" value={stats?.cars?.disponibili || 0} accent testid="m-disp" />
        <Mini icon={FileSignature} label="Noleggiate" value={stats?.cars?.noleggiate || 0} testid="m-nol" />
        <Mini icon={Wrench} label="Guasto/Manut." value={stats?.cars?.manutenzione || 0} testid="m-manut" />
        <Mini icon={Ban} label="Fuori Flotta" value={cars.filter(c => c.stato === 'fuori_flotta' || c.stato === 'fuori_servizio').length} testid="m-fuori" />
        <Mini icon={FileSignature} label="Contratti Attivi" value={extras.contracts?.filter(c => c.stato === 'attivo').length || 0} testid="m-contr" />
        <Mini icon={Euro} label="Incassi Oggi" value={`€${Math.round((stats?.revenue || 0) / 30)}`} accent testid="m-today" />
        <Mini icon={ArrowDownToLine} label="Rientri Scaduti" value={rientriScaduti} testid="m-rientri" />
        <Mini icon={Users} label="Clienti Totali" value={stats?.customers?.totale || 0} testid="m-clienti" />
        <Mini icon={TrendingUp} label="Incassi Mensili" value={`€${(stats?.revenue || 0).toLocaleString("it-IT")}`} testid="m-mensili" />
        <Mini icon={CalendarRange} label="Prenotazioni" value={stats?.bookings?.totale || 0} testid="m-pren" />
        <Mini icon={AlertTriangle} label="Doc. in scadenza" value={scadenzeDocumenti} testid="m-scadenze" />
      </div>

      {/* === RISPARMIO MULTE — KPI banner === */}
      {risparmio?.ok && (risparmio.risparmio_totale_eur > 0) && (
        <motion.a
          href="/admin/rinotifiche"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="block relative overflow-hidden glass rounded-2xl p-5 border border-emerald-500/30 hover:border-emerald-500/60 transition-all group"
          data-testid="kpi-risparmio-multe"
        >
          <div className="absolute -right-12 -top-12 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl group-hover:bg-emerald-500/20 transition-all" />
          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                <Gavel className="text-emerald-400" size={22} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/80">💸 Risparmio Multe — Auto-Rinotifica</div>
                <div className="font-display text-3xl font-black tracking-tight mt-0.5">
                  € {(risparmio.risparmio_totale_eur || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-white/50 mt-1">
                  scaricati / in elaborazione dal carico società grazie al sistema PEC
                </div>
              </div>
            </div>
            <div className="hidden md:flex gap-4 text-right text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40">Consegnati</div>
                <div className="font-mono text-lg text-emerald-400 font-bold">€ {(risparmio.risparmio_consegnate_eur || 0).toLocaleString("it-IT")}</div>
                <div className="text-[10px] text-white/30">{risparmio.pratiche_consegnate} pratiche ✓</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40">In corso</div>
                <div className="font-mono text-lg text-blue-400 font-bold">€ {(risparmio.risparmio_in_corso_eur || 0).toLocaleString("it-IT")}</div>
                <div className="text-[10px] text-white/30">{risparmio.pratiche_in_corso} inviate</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40">Potenziale</div>
                <div className="font-mono text-lg text-amber-400 font-bold">€ {(risparmio.risparmio_potenziale_eur || 0).toLocaleString("it-IT")}</div>
                <div className="text-[10px] text-white/30">{risparmio.pratiche_pending} da elaborare</div>
              </div>
            </div>
          </div>
        </motion.a>
      )}

      {/* === COMPLIANCE HEALTH WIDGET === */}
      {compliance?.status?.ok && (
        <motion.a
          href="/admin/compliance"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="block relative overflow-hidden glass rounded-2xl p-5 border border-[#ff0033]/30 hover:border-[#ff0033]/60 transition-all group"
          data-testid="kpi-compliance-health"
        >
          <div className="absolute -right-12 -top-12 w-48 h-48 bg-[#ff0033]/10 rounded-full blur-3xl group-hover:bg-[#ff0033]/20 transition-all" />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#ff0033]/15 border border-[#ff0033]/30 flex items-center justify-center shrink-0">
                <Shield className="text-[#ff0033]" size={22} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff0033]/80">🛡️ Compliance Health — 5 Regole Enterprise</div>
                <div className="font-display text-base sm:text-xl font-black tracking-tight mt-0.5">
                  Sistema antifrode operativo · scheduler 48h attivo
                </div>
                <div className="text-[10px] sm:text-xs text-white/50 mt-1">
                  Tourist filter · Credit escrow · Deferred release · Pre-existing damages · Global blacklist
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:flex gap-2 md:gap-4 text-right text-xs flex-wrap w-full md:w-auto">
              <div className={`px-3 py-2 rounded-lg border ${compliance.review_pending > 0 ? "bg-amber-500/10 border-amber-500/40" : "bg-white/5 border-white/10"}`}>
                <div className="text-[10px] uppercase tracking-widest text-white/40 inline-flex items-center gap-1"><AlertTriangle size={10} /> Review Queue</div>
                <div className={`font-mono text-lg font-bold ${compliance.review_pending > 0 ? "text-amber-300" : "text-white/70"}`}>{compliance.review_pending}</div>
                <div className="text-[10px] text-white/30">{compliance.review_pending === 1 ? "booking" : "booking"} da approvare</div>
              </div>
              <div className="px-3 py-2 rounded-lg border bg-rose-500/10 border-rose-500/40">
                <div className="text-[10px] uppercase tracking-widest text-white/40 inline-flex items-center gap-1"><ShieldAlert size={10} /> Blacklist</div>
                <div className="font-mono text-lg text-rose-300 font-bold">{compliance.status.rules['5_global_fraud_blacklist']?.total_hashed ?? 0}</div>
                <div className="text-[10px] text-white/30">soggetti hashed</div>
              </div>
              <div className="px-3 py-2 rounded-lg border bg-yellow-500/10 border-yellow-500/40">
                <div className="text-[10px] uppercase tracking-widest text-white/40 inline-flex items-center gap-1"><Clock size={10} /> Hold 48h</div>
                <div className="font-mono text-lg text-yellow-300 font-bold">{compliance.status.rules['3_deferred_release_48h']?.deferred_active ?? 0}</div>
                <div className="text-[10px] text-white/30">depositi in attesa</div>
              </div>
              <div className="px-3 py-2 rounded-lg border bg-orange-500/10 border-orange-500/40">
                <div className="text-[10px] uppercase tracking-widest text-white/40 inline-flex items-center gap-1"><AlertCircle size={10} /> Baseline</div>
                <div className="font-mono text-lg text-orange-300 font-bold">{compliance.status.rules['4_pre_existing_damages']?.baseline_records ?? 0}</div>
                <div className="text-[10px] text-white/30">danni pre-esistenti</div>
              </div>
            </div>
          </div>
        </motion.a>
      )}

      {/* === OPERATIONAL TREND (30gg) === */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass rounded-2xl p-5" data-testid="ops-trend-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Operational Trend</div>
              <div className="font-display text-lg font-bold mt-0.5">Ultimi 30 giorni · Booking · Contratti · Compliance</div>
            </div>
            <Activity className="text-[#ff0033]" size={16} />
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={opsTrend}>
              <defs>
                <linearGradient id="bk" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="ct" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="cmp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ff0033" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#ff0033" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1c1c1f" vertical={false} />
              <XAxis dataKey="date" stroke="#666" fontSize={9} tickFormatter={(d)=>d.slice(5)} />
              <YAxis stroke="#666" fontSize={10} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #ff0033", borderRadius: 8 }} />
              <Area type="monotone" dataKey="bookings" name="Booking" stroke="#3b82f6" strokeWidth={2} fill="url(#bk)" />
              <Area type="monotone" dataKey="contracts" name="Contratti" stroke="#22c55e" strokeWidth={2} fill="url(#ct)" />
              <Area type="monotone" dataKey="compliance_events" name="Eventi compliance" stroke="#ff0033" strokeWidth={2} fill="url(#cmp)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="glass rounded-2xl p-5" data-testid="risk-distribution">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Risk Distribution</div>
              <div className="font-display text-lg font-bold mt-0.5">Clienti per fascia</div>
            </div>
            <ShieldCheck className="text-[#ff0033]" size={16} />
          </div>
          {riskDist?.distribution ? (
            <div className="space-y-2">
              {[
                { k: 'critical', label: 'Critical', color: '#ef4444' },
                { k: 'high', label: 'High', color: '#f97316' },
                { k: 'medium', label: 'Medium', color: '#facc15' },
                { k: 'low', label: 'Low (sicuri)', color: '#22c55e' },
                { k: 'unknown', label: 'Non valutati', color: '#64748b' },
              ].map((r) => {
                const v = riskDist.distribution[r.k] || 0;
                const pct = riskDist.total_evaluated > 0 ? (v / riskDist.total_evaluated * 100) : 0;
                return (
                  <div key={r.k}>
                    <div className="flex items-center justify-between text-xs">
                      <span>{r.label}</span>
                      <span className="font-mono text-white/60">{v} · {pct.toFixed(0)}%</span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mt-1">
                      <div className="h-full transition-all" style={{ width: `${pct}%`, background: r.color }} />
                    </div>
                  </div>
                );
              })}
              <div className="text-[10px] text-white/30 pt-2 border-t border-white/5 mt-3">Tot. valutati: {riskDist.total_evaluated}</div>
            </div>
          ) : (
            <div className="text-xs text-white/40">Nessun cliente ancora valutato. Vai su Clienti → &quot;Ricalcola Risk Score&quot;.</div>
          )}
        </div>
      </div>

      {/* === REGISTRO LIVE ASSEGNAZIONE VEICOLI === */}
      <LiveFleetTable />

      {/* === CONTABILITÀ DI CASSA SETTIMANALE === */}
      <WeeklyCash />

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass rounded-2xl p-5" data-testid="revenue-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Entrate</div>
              <div className="font-display text-lg font-bold mt-0.5">Ultimi 6 mesi</div>
            </div>
            <Activity className="text-[#ff0033]" size={16} />
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={stats?.revenue_chart || []}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ff0033" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#ff0033" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1c1c1f" vertical={false} />
              <XAxis dataKey="mese" stroke="#666" fontSize={10} />
              <YAxis stroke="#666" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #ff0033", borderRadius: 8 }} />
              <Area type="monotone" dataKey="importo" stroke="#ff0033" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="glass rounded-2xl p-5" data-testid="weekly">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Incassi Settimana</div>
              <div className="font-display text-lg font-bold mt-0.5">Lun → Dom</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/40">Totale</div>
              <div className="font-display text-xl neon-text font-extrabold">€{weekly?.tot?.toLocaleString("it-IT") || 0}</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={170}>
            <BarChart data={weekly?.daily || []}>
              <CartesianGrid stroke="#1c1c1f" vertical={false} />
              <XAxis dataKey="giorno" stroke="#666" fontSize={10} />
              <YAxis stroke="#666" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #ff0033", borderRadius: 8 }} />
              <Bar dataKey="importo" fill="#ff0033" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Alerts + Top */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass rounded-2xl p-5" data-testid="top-cars">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Top Auto</div>
              <div className="font-display text-lg font-bold mt-0.5">Più noleggiate</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats?.top_cars || []}>
              <CartesianGrid stroke="#1c1c1f" vertical={false} />
              <XAxis dataKey="auto" stroke="#666" fontSize={10} />
              <YAxis stroke="#666" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #ff0033", borderRadius: 8 }} />
              <Bar dataKey="noleggi" fill="#ff0033" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="glass rounded-2xl p-5" data-testid="alerts">
          <div className="flex items-center gap-2 text-[#ff0033] mb-4">
            <AlertCircle size={14} />
            <div className="text-[10px] uppercase tracking-[0.3em]">Alert</div>
          </div>
          <ul className="space-y-2.5 text-xs">
            {extras.maint?.filter(m => m.stato === "programmata").slice(0, 3).map((m) => (
              <li key={m.id} className="flex items-start gap-2 pb-2 border-b border-white/5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium truncate">{m.tipo} {m.car?.marca}</div>
                  <div className="text-white/40 text-[10px] font-mono">{m.car?.targa} · {m.data}</div>
                </div>
              </li>
            ))}
            {rientriScaduti > 0 && (
              <li className="flex items-start gap-2 pb-2 border-b border-white/5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#ff0033] mt-1.5 shrink-0" />
                <div>
                  <div className="font-medium">{rientriScaduti} rientri scaduti</div>
                  <div className="text-white/40 text-[10px]">Contratti oltre la data fine</div>
                </div>
              </li>
            )}
            {scadenzeDocumenti > 0 && (
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#ff0033] mt-1.5 shrink-0" />
                <div>
                  <div className="font-medium">{scadenzeDocumenti} doc. in scadenza 30gg</div>
                  <div className="text-white/40 text-[10px]">Bolli/Assicurazioni/Revisioni</div>
                </div>
              </li>
            )}
            {extras.maint?.length === 0 && rientriScaduti === 0 && scadenzeDocumenti === 0 && (
              <li className="text-white/40 text-center py-4">Nessun alert</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
